/**
 * DaysRemainingStrip — horizontal two-card strip showing days left in the
 * current month and current quarter.
 *
 * Color thresholds
 *   Month:   green >=10 / yellow 5-9 / amber 2-4 / red 0-1
 *   Quarter: green >=30 / yellow 15-29 / amber 5-14 / red 0-4
 *
 * Visual language: light bg #F9FAFB, white cards, #E5E7EB borders, 8px radius.
 * Pure inline styles — no Tailwind.
 */

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function getQuarterName(month: number, year: number): string {
  const q = Math.ceil(month / 3);
  return `Q${q} ${year}`;
}

function lastDayOfMonth(year: number, month: number): Date {
  // month is 1-based
  return new Date(year, month, 0); // day 0 of next month = last day of this month
}

function lastDayOfQuarter(year: number, month: number): Date {
  const q = Math.ceil(month / 3);
  const lastMonthOfQ = q * 3;
  return new Date(year, lastMonthOfQ, 0);
}

/** Strip today's time component and return midnight-local. */
function todayMidnight(): Date {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function diffDays(from: Date, to: Date): number {
  const ms = to.getTime() - from.getTime();
  return Math.round(ms / 86_400_000);
}

interface ColorScheme {
  accent: string;   // bar / big-number color
  cardBg: string;
  labelColor: string;
}

function monthColor(days: number): ColorScheme {
  if (days >= 10)
    return { accent: "#059669", cardBg: "#ECFDF5", labelColor: "#065F46" };
  if (days >= 5)
    return { accent: "#CA8A04", cardBg: "#FFFBEB", labelColor: "#92400E" };
  if (days >= 2)
    return { accent: "#B45309", cardBg: "#FFF7ED", labelColor: "#9A3412" };
  return { accent: "#DC2626", cardBg: "#FEF2F2", labelColor: "#991B1B" };
}

function quarterColor(days: number): ColorScheme {
  if (days >= 30)
    return { accent: "#059669", cardBg: "#ECFDF5", labelColor: "#065F46" };
  if (days >= 15)
    return { accent: "#CA8A04", cardBg: "#FFFBEB", labelColor: "#92400E" };
  if (days >= 5)
    return { accent: "#B45309", cardBg: "#FFF7ED", labelColor: "#9A3412" };
  return { accent: "#DC2626", cardBg: "#FEF2F2", labelColor: "#991B1B" };
}

interface CardData {
  label: string;        // e.g. "Days left in month"
  periodName: string;   // e.g. "May" or "Q2 2026"
  daysLeft: number;
  daysUsed: number;
  totalDays: number;
  scheme: ColorScheme;
}

function DaysCard({ card }: { card: CardData }) {
  const pctUsed = card.totalDays > 0
    ? Math.min(100, Math.round((card.daysUsed / card.totalDays) * 100))
    : 0;

  return (
    <div
      style={{
        flex: 1,
        background: "#FFFFFF",
        border: "1px solid #E5E7EB",
        borderRadius: 8,
        padding: 16,
        minWidth: 0,
      }}
    >
      {/* Label row */}
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.12em",
          color: "#6B7280",
          marginBottom: 8,
        }}
      >
        {card.label}
      </div>

      {/* Big number + period name */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
        <span
          style={{
            fontSize: 48,
            fontWeight: 700,
            lineHeight: 1,
            color: card.scheme.accent,
          }}
        >
          {card.daysLeft}
        </span>
        <span
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
          }}
        >
          {card.periodName}
        </span>
      </div>

      {/* Progress bar */}
      <div
        style={{
          height: 6,
          background: "#E5E7EB",
          borderRadius: 3,
          overflow: "hidden",
          marginBottom: 8,
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${pctUsed}%`,
            background: card.scheme.accent,
            borderRadius: 3,
            transition: "width 0.4s ease",
          }}
        />
      </div>

      {/* Subtitle */}
      <div style={{ fontSize: 12, color: "#6B7280" }}>
        {card.daysUsed} of {card.totalDays} days used ({pctUsed}%)
      </div>
    </div>
  );
}

export default function DaysRemainingStrip() {
  const today = todayMidnight();
  const year = today.getFullYear();
  const month = today.getMonth() + 1; // 1-based
  const day = today.getDate();

  const monthEnd = lastDayOfMonth(year, month);
  const quarterEnd = lastDayOfQuarter(year, month);

  const daysLeftMonth = diffDays(today, monthEnd);
  const daysLeftQuarter = diffDays(today, quarterEnd);

  // Defensive: render nothing if we're past the end of period
  if (daysLeftMonth < 0 || daysLeftQuarter < 0) return null;

  const monthStart = new Date(year, month - 1, 1);
  const totalDaysMonth = diffDays(monthStart, monthEnd) + 1; // inclusive
  const daysUsedMonth = day - 1; // days before today

  const q = Math.ceil(month / 3);
  const quarterStartMonth = (q - 1) * 3 + 1;
  const quarterStart = new Date(year, quarterStartMonth - 1, 1);
  const totalDaysQuarter = diffDays(quarterStart, quarterEnd) + 1;
  const daysUsedQuarter = diffDays(quarterStart, today);

  const monthCard: CardData = {
    label: "Days left in month",
    periodName: MONTH_NAMES[month - 1],
    daysLeft: daysLeftMonth,
    daysUsed: daysUsedMonth,
    totalDays: totalDaysMonth,
    scheme: monthColor(daysLeftMonth),
  };

  const quarterCard: CardData = {
    label: "Days left in quarter",
    periodName: getQuarterName(month, year),
    daysLeft: daysLeftQuarter,
    daysUsed: daysUsedQuarter,
    totalDays: totalDaysQuarter,
    scheme: quarterColor(daysLeftQuarter),
  };

  return (
    <div
      style={{
        display: "flex",
        gap: 16,
        marginBottom: 24,
      }}
    >
      <DaysCard card={monthCard} />
      <DaysCard card={quarterCard} />
    </div>
  );
}
